/**
 * 经营概览页（`/overview`）的时间范围 URL 状态（2026-10-05 第十五批 · 用户第 3 条）。
 *
 * 用户原话：「概览现在不能选择 今天、昨天、7 天、30 天、90 天等等类似于广告管理的时间范围选择。
 * 另外，概览一般**默认显示最近 30 天**，这 30 天**不应该包括今天**，也就是 **-1 ~ -31**」。
 *
 * 因此本页：
 *  · **复用**广告管理的同一套快捷选项（`dateRange.ts`）与同一套 URL 解析/序列化
 *    （`rangeSearch.ts`），不另写第二套区间定义；
 *  · 默认档 = `last30`（**不是**全站默认档 `last7`）→ `[今天-30, 今天-1]`，不含今天；
 *  · `from` / `to` / `range` 都进 URL（可分享、刷新还原）。
 *
 * 为什么默认档写成常量而不是各页各写：广告管理默认 7 天、概览默认 30 天，
 * 这是**两个页面的默认值**，但「最近 N 天」的**区间算法**只有一份
 * （`resolveDateRange`：`[今天-N, 今天-1]`）。
 */

import {
  defaultRangeSearch,
  parseRangeSearch,
  rangeSearchToParams,
  RANGE_SEARCH_KEYS,
  type RangeSearch,
} from "./rangeSearch.js";

/** 概览页默认档 = 最近 30 天（用户第 3 条）。 */
export const OVERVIEW_DEFAULT_RANGE_KEY = "last30";
/** 概览页默认窗口天数（与 `last30` 同义；仅用于文案/对照，参与计算的是 preset）。 */
export const OVERVIEW_DEFAULT_WINDOW_DAYS = 30;
/** 概览页 search 的 key 集合（与广告管理的共享三件套相同）。 */
export const OVERVIEW_SEARCH_KEYS: readonly string[] = RANGE_SEARCH_KEYS;

export type OverviewSearch = RangeSearch;

/** 默认区间 = 最近 30 天（`[今天-30, 今天-1]`，**不含今天**）。 */
export function defaultOverviewSearch(now: Date = new Date()): OverviewSearch {
  return defaultRangeSearch(OVERVIEW_DEFAULT_RANGE_KEY, now);
}

/** URL → 区间（垃圾输入回落默认；区间定义见 `dateRange.ts`）。 */
export function parseOverviewSearch(raw: Record<string, unknown>, now: Date = new Date()): OverviewSearch {
  return parseRangeSearch(raw, OVERVIEW_DEFAULT_RANGE_KEY, now);
}

/**
 * 序列化：与广告管理页共用 `rangeSearchToParams`（`from`/`to` 总是写出，
 * `range` 也**总是写出**（2026-10-05 第十七批；理由见 `rangeSearchToParams`）。
 */
export function overviewSearchToParams(search: OverviewSearch): Record<string, string> {
  return rangeSearchToParams(search);
}

/** 供 TanStack Router 的 `stringifySearch` 使用。 */
export function overviewSearchToQueryString(search: object): string {
  const parsed = parseOverviewSearch(search as Record<string, unknown>);
  const queryString = new URLSearchParams(overviewSearchToParams(parsed)).toString();
  return queryString.length > 0 ? `?${queryString}` : "";
}
